import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button, 
    TextField, 
    Select, 
    MenuItem, 
    FormControl, 
    InputLabel, 
    Grid, 
    Alert, 
    Typography, 
    List, 
    ListItem, 
    ListItemText, 
    IconButton, 
    SelectChangeEvent, 
    AppBar, 
    Toolbar, 
    Box, 
    Switch, 
    FormControlLabel, 
    Link, 
    CircularProgress, 
    Autocomplete, 
    Chip,
    Tabs,
    Tab,
    Paper,
    ListSubheader,
    Radio,
    RadioGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoneyIcon from '@mui/icons-material/Money';
import SendIcon from '@mui/icons-material/Send';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import QueueIcon from '@mui/icons-material/Queue';
import BroadcastOnPersonalIcon from '@mui/icons-material/BroadcastOnPersonal';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import CampaignIcon from '@mui/icons-material/Campaign';
import { Event, Participant } from '../types/Event';
import venueData from '../allVenue_BADC.json';
import { green } from '@mui/material/colors';
import WhatsAppMessagePreview from './WhatsAppMessagePreview';
import DuplicateEventModal from './DuplicateEventModal';
import PreviewIcon from '@mui/icons-material/Preview';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { API_BASE_URL } from '../config';

interface Venue {
    venueName: string;
    distCode: string;
}

interface GroupedVenues {
    [key: string]: Venue[];
}

// Manage-activity functions are grouped into three purpose-based categories so
// admins can tell at a glance which section to use. Each leaf "view" maps to one
// of the existing render functions.
type ViewKey =
    | 'details'
    | 'participants'
    | 'waiting'
    | 'batch'
    | 'broadcast'
    | 'announcements';

const groupVenuesByDistrict = (venues: Venue[]): GroupedVenues => {
    return venues.reduce((acc: GroupedVenues, venue) => {
        if (!acc[venue.distCode]) {
            acc[venue.distCode] = [];
        }
        acc[venue.distCode].push(venue);
        return acc;
    }, {});
};

const groupedVenues = groupVenuesByDistrict(venueData);

interface Props {
    event: Event;
    onClose: () => void;
    onEventUpdated: (updatedEvent: Event) => void;
    onEventDeleted: (eventId: string) => void;
    siteName: string;
    sitePermissions?: any;
}

interface PreviousParticipant {
    name: string;
    phone: string;
}

const ManageEventModal: React.FC<Props> = ({ event, onClose, onEventUpdated, onEventDeleted, siteName, sitePermissions }) => {
    const [editedEvent, setEditedEvent] = useState<Event>({ ...event });
    const [editingParticipant, setEditingParticipant] = useState<Participant | null>(null);
    const [participantToRemove, setParticipantToRemove] = useState<Participant | null>(null);
    const [isRemovingParticipant, setIsRemovingParticipant] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [showEditDelete, setShowEditDelete] = useState(false);
    const [view, setView] = useState<ViewKey>('details');
    const hours = Array.from({ length: 17 }, (_, i) => (i + 7).toString().padStart(2, '0'));
    const [isDeleting, setIsDeleting] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [isBroadcasting, setIsBroadcasting] = useState(false);
    const [isSendingPaymentLinks, setIsSendingPaymentLinks] = useState(false);
    const [showPaymentPreview, setShowPaymentPreview] = useState(false);
    const [siteSettings, setSiteSettings] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isParticipantSaving, setIsParticipantSaving] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
    const dialogContentRef = useRef<HTMLDivElement>(null);
    const [batchParticipants, setBatchParticipants] = useState<Participant[]>([]);
    const [newParticipant, setNewParticipant] = useState<Participant>({ _id: '', name: '', phone: '', spots: 1, fee: 0, checkedIn: false, paid: false, approved: true });
    const [availableSpots, setAvailableSpots] = useState<number>(event.maxParticipants - (Array.isArray(event.participants) ? event.participants.reduce((sum, p) => sum + p.spots, 0) : 0));
    const [previousParticipants, setPreviousParticipants] = useState<PreviousParticipant[]>([]);
    const [isLoadingPreviousParticipants, setIsLoadingPreviousParticipants] = useState(false);
    const [showBroadcastPreview, setShowBroadcastPreview] = useState(false);
    const [joinEventPreview, setJoinEventPreview] = useState('載入中...');
    const [announcementTitle, setAnnouncementTitle] = useState('');
    const [announcementMessage, setAnnouncementMessage] = useState('');
    const [isImportant, setIsImportant] = useState(false);
    const [announcementVisibility, setAnnouncementVisibility] = useState<'public' | 'private'>('public');
    const [isCreatingAnnouncement, setIsCreatingAnnouncement] = useState(false);
    const [isCustomLocation, setIsCustomLocation] = useState(false);
    const [customLocation, setCustomLocation] = useState('');

    useEffect(() => {
        const fetchPreviousParticipants = async () => {
            setIsLoadingPreviousParticipants(true);
            try {
                // Extract site ID from event (could be string or populated object)
                const siteId = typeof event.site === 'string' ? event.site : event.site?._id;
                const url = siteId 
                    ? `${API_BASE_URL}/events/previous-participants?siteId=${siteId}`
                    : `${API_BASE_URL}/events/previous-participants`;
                
                const response = await axios.get(url, {
                    headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
                });
                setPreviousParticipants(response.data);
            } catch (error) {
                console.error('Error fetching previous participants:', error);
            } finally {
                setIsLoadingPreviousParticipants(false);
            }
        };

        fetchPreviousParticipants();
    }, [event.site]);

    // Fetch site settings when component mounts
    useEffect(() => {
        const loadSiteSettings = async () => {
            await fetchSiteSettings();
        };
        loadSiteSettings();
    }, []); // Empty dependency array - only run once on mount

    // Fetch join-event message preview
    useEffect(() => {
        const fetchJoinEventPreview = async () => {
            console.log('Fetching join-event preview...', { 
                hasEditedEvent: !!editedEvent, 
                hasSiteSettings: !!siteSettings,
                defaultSenderId: siteSettings?.defaultSenderId
            });

            if (!editedEvent) {
                console.log('No editedEvent available for join-event preview');
                return;
            }

            const contactNumber = siteSettings?.defaultSenderId || '聯絡電話';
            const sampleParticipant = {
                name: 'John',
                spots: 1,
                fee: editedEvent.splitFee ? (editedEvent.totalCost || 0) / Math.max(editedEvent.maxParticipants, 1) : editedEvent.memberFee
            };

            // Set a fallback message immediately in case API is slow
            const clientSidePreview = `[來自${siteName}的訊息] 
你好 ${sampleParticipant.name}, 你的報名已確認 ⭐️

日期: ${editedEvent.date}
時間: ${editedEvent.time}
地點: ${editedEvent.location}
詳情:
${editedEvent.sendDetails || ''}

有需要可聯絡此號碼: ${contactNumber}`;
            
            // Set fallback first, then try to get better version from API
            setJoinEventPreview(clientSidePreview);

            try {
                console.log('Making API call to:', `${API_BASE_URL}/templates/join-event/preview`);
                console.log('With data:', {
                    participantName: sampleParticipant.name,
                    eventDate: editedEvent.date,
                    eventTime: editedEvent.time,
                    eventLocation: editedEvent.location,
                    eventDetails: editedEvent.sendDetails || '',
                    contactNumber: contactNumber,
                    siteName: siteName
                });

                const response = await axios.post(`${API_BASE_URL}/templates/join-event/preview`, {
                    participantName: sampleParticipant.name,
                    eventDate: editedEvent.date,
                    eventTime: editedEvent.time,
                    eventLocation: editedEvent.location,
                    eventDetails: editedEvent.sendDetails || '',
                    contactNumber: contactNumber,
                    siteName: siteName
                }, {
                    timeout: 5000 // 5 second timeout
                });
                
                console.log('Join-event API response:', response.data);
                if (response.data && response.data.preview) {
                    setJoinEventPreview(response.data.preview);
                } else {
                    console.warn('API response missing preview field:', response.data);
                    // Keep fallback message
                }
            } catch (error: any) {
                console.error('Error fetching join-event preview, using fallback:', error);
                console.error('Error details:', error.response?.data || error.message);
            }
        };

        fetchJoinEventPreview();
    }, [editedEvent, siteSettings, siteName]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent) => {
        const { name, value } = e.target;
        if (name === 'fromHour' || name === 'toHour') {
            const [fromHour, toHour] = editedEvent.time.split(' - ');
            const newTime = name === 'fromHour'
                ? `${value}:00 - ${toHour}`
                : `${fromHour} - ${value}:00`;
            setEditedEvent(prev => ({ ...prev, time: newTime }));
        } else if (name === 'isPublic' || name === 'isPrivate') {
            const checked = (e.target as HTMLInputElement).checked;
            // Public (homepage) and private (link-only) are mutually exclusive.
            setEditedEvent(prev => ({
                ...prev,
                [name]: checked,
                ...(checked && name === 'isPublic' ? { isPrivate: false } : {}),
                ...(checked && name === 'isPrivate' ? { isPublic: false } : {})
            }));
        } else if (name === 'isSendWhatsapp' || name === 'splitFee') {
            setEditedEvent(prev => ({ ...prev, [name]: (e.target as HTMLInputElement).checked }));
        } else {
            setEditedEvent(prev => ({ 
                ...prev, 
                [name]: name === 'memberFee' || name === 'normalFee' || name === 'totalCost' ? parseFloat(value) : value 
            }));
        }
    };

    const handleParticipantInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setEditingParticipant(prev => prev ? {
            ...prev,
            [name]: name === 'spots' ? parseInt(value) : value
        } : null);
    };

    const validateEditedEvent = (): boolean => {
        const [fromTime, toTime] = editedEvent.time.split(' - ');
        if (!fromTime || !toTime || toTime <= fromTime) {
            setError('結束時間必須晚於開始時間');
            return false;
        }
        const max = Number(editedEvent.maxParticipants);
        if (!max || max < 1) {
            setError('最大參與人數必須大於0');
            return false;
        }
        const enrolled = Array.isArray(editedEvent.participants)
            ? editedEvent.participants.reduce((sum, p) => sum + p.spots, 0)
            : 0;
        if (max < enrolled) {
            setError(`最大參與人數不能少於目前已報名人數 (${enrolled})`);
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        setError(null);
        if (!validateEditedEvent()) {
            if (dialogContentRef.current) {
                dialogContentRef.current.scrollTop = 0;
            }
            return;
        }
        setIsSaving(true);
        try {
            await axios.put(`${API_BASE_URL}/events/${event._id}`, editedEvent, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            
            // Fetch the updated event to get the latest participant fees
            const updatedEventResponse = await axios.get(`${API_BASE_URL}/events/${event._id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            const updatedEvent = updatedEventResponse.data;
            
            // Update local state with the fresh data from server
            setEditedEvent(updatedEvent);
            
            // Update available spots calculation
            setAvailableSpots(updatedEvent.maxParticipants - (Array.isArray(updatedEvent.participants) ? updatedEvent.participants.reduce((sum: number, p: { spots: number }) => sum + p.spots, 0) : 0));
            
            setSuccess('活動已成功更新！');
            onEventUpdated(updatedEvent);
            // Scroll to top after successful save
            if (dialogContentRef.current) {
                dialogContentRef.current.scrollTop = 0;
            }
        } catch (err: any) {
            console.error('Error updating event:', err);
            setError(err.response?.data?.message || '更新活動失敗請稍後再試。');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveParticipant = async () => {
        if (!editingParticipant) return;
        setIsParticipantSaving(true);
        try {
            const response = await axios.put(
                `${API_BASE_URL}/events/${editedEvent._id}/participants/${editingParticipant._id}`,
                editingParticipant,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            setEditedEvent(response.data.event);
            setEditingParticipant(null);
            onEventUpdated(response.data.event);
        } catch (error) {
            console.error('Error updating participant:', error);
            setError('更新參與者失敗。請稍後再試。');
        } finally {
            setIsParticipantSaving(false);
        }
    };

    const confirmRemoveParticipant = async () => {
        if (!participantToRemove) return;
        setIsRemovingParticipant(true);
        await handleRemoveParticipant(participantToRemove._id);
        setIsRemovingParticipant(false);
        setParticipantToRemove(null);
    };

    const handleRemoveParticipant = async (participantId: string) => {
        try {
            const response = await axios.delete(`${API_BASE_URL}/events/${editedEvent._id}/participants/${participantId}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            setEditedEvent(response.data.event);
            onEventUpdated(response.data.event);
        } catch (error) {
            console.error('Error removing participant:', error);
            setError('移除參與者失敗。請稍後再試。');
        }
    };

    const handleCheckIn = async (participantId: string) => {
        // Optimistically update the UI
        setEditedEvent(prev => ({
            ...prev,
            participants: prev.participants.map(p => 
                p._id === participantId ? { ...p, checkedIn: !p.checkedIn } : p
            )
        }));

        try {
            const response = await axios.put(`${API_BASE_URL}/events/${editedEvent._id}/participants/${participantId}/checkin`, undefined, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            // Update with the server response to ensure consistency
            setEditedEvent(response.data.event);
        } catch (error) {
            console.error('Error checking in participant:', error);
            setError('簽到失敗。請稍後再試。');
            // Revert the optimistic update
            setEditedEvent(prev => ({
                ...prev,
                participants: prev.participants.map(p => 
                    p._id === participantId ? { ...p, checkedIn: !p.checkedIn } : p
                )
            }));
        }
    };

    const handlePaid = async (participantId: string) => {
        // Optimistically update the UI
        setEditedEvent(prev => ({
            ...prev,
            participants: prev.participants.map(p => 
                p._id === participantId ? { ...p, paid: !p.paid } : p
            )
        }));

        try {
            const response = await axios.put(`${API_BASE_URL}/events/${editedEvent._id}/participants/${participantId}/paid`, undefined, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            // Update with the server response to ensure consistency
            setEditedEvent(response.data.event);
        } catch (error) {
            console.error('Error marking participant as paid:', error);
            setError('標記付款失敗。請稍後再試。');
            // Revert the optimistic update
            setEditedEvent(prev => ({
                ...prev,
                participants: prev.participants.map(p => 
                    p._id === participantId ? { ...p, paid: !p.paid } : p
                )
            }));
        }
    };

    const getWhatsAppLink = (phone: string) => {
    // Remove any non-digit characters from the phone number
        if (phone === undefined) return '';
        const cleanPhone = phone.replace(/\D/g, '');
        return `https://wa.me/852${cleanPhone}`;
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await axios.delete(`${API_BASE_URL}/events/${event._id}`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            setSuccess('活動已成功刪除！');
            onEventDeleted(event._id);
        } catch (err: any) {
            console.error('Error deleting event:', err);
            setError(err.response?.data?.message || '刪除活動失敗，請稍後再試。');
        } finally {
            setIsDeleting(false);
            setShowDeleteConfirmation(false);
        }
    };

    const handleBroadcast = async () => {
        if (!broadcastMessage.trim()) {
            setError('請輸入要廣播的訊息');
            return;
        }

        setIsBroadcasting(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await axios.post(`${API_BASE_URL}/events/${event._id}/broadcast`, { message: broadcastMessage }, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            setSuccess('廣播訊息發送成功');
            setBroadcastMessage('');
            setShowBroadcastPreview(false);

            if (response.status === 207) {
                setError(`廣播訊息已發送，但部分發送失敗。失敗的接收者：${response.data.failedRecipients.join(', ')}`);
            }
        } catch (err: any) {
            console.error('廣播訊息發送錯誤:', err);
            setError(err.response?.data?.message || '廣播訊息發送失敗。請稍後再試。');
        } finally {
            setIsBroadcasting(false);
        }
    };

    const handleBroadcastWithPreview = async () => {
        if (!siteSettings) {
            await fetchSiteSettings();
        }
        setShowBroadcastPreview(false);
        await handleBroadcast();
    };

    const fetchSiteSettings = async () => {
        try {
            const response = await axios.get(
                `${API_BASE_URL}/sites/my-site`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            setSiteSettings(response.data.settings);
            return response.data.settings;
        } catch (err: any) {
            console.error('獲取群組設定錯誤:', err);
            setError('無法載入群組設定');
            return null;
        }
    };

    const handleShowPaymentPreview = async () => {
        if (!siteSettings) {
            await fetchSiteSettings();
        }
        setShowPaymentPreview(true);
    };

    const handleSendPaymentLinks = async () => {
        setIsSendingPaymentLinks(true);
        setError(null);
        setSuccess(null);

        try {
            const response = await axios.post(
                `${API_BASE_URL}/sites/send-payment-links`,
                { eventId: event._id },
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            
            setSuccess(response.data.message);

            if (response.status === 207) {
                setError(`付款連結已發送，但部分發送失敗。失敗的接收者：${response.data.failedRecipients?.join(', ')}`);
            }
        } catch (err: any) {
            console.error('發送付款連結錯誤:', err);
            setError(err.response?.data?.message || '發送付款連結失敗。請稍後再試。');
        } finally {
            setIsSendingPaymentLinks(false);
            setShowPaymentPreview(false); // Close preview after sending
        }
    };



    const handleMoveFromWaitingList = async (participantId: string) => {
        try {
            const response = await axios.put(`${API_BASE_URL}/events/${editedEvent._id}/waitinglist/${participantId}/move`, undefined, { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
            setEditedEvent(response.data.event);
            onEventUpdated(editedEvent);
        } catch (error: any) {
            console.error('Error moving participant from waiting list:', error);
            if (axios.isAxiosError(error) && error.response && error.response.status === 400) {
                setError(error.response.data.message || '移動參加者失敗。請稍後再試。');
            } else {
                setError('移動參加者失敗。請稍後再試。');
            }
        }
    };

    const handleBatchParticipantChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewParticipant(prev => ({
            ...prev,
            [name]: name === 'spots' ? parseInt(value) : value
        }));
    };

    const handleNameSelect = (event: React.SyntheticEvent, value: string | PreviousParticipant | null) => {
        if (value && typeof value !== 'string') {
            setNewParticipant(prev => ({
                ...prev,
                name: value.name,
                phone: value.phone
            }));
        } else if (typeof value === 'string') {
            setNewParticipant(prev => ({
                ...prev,
                name: value
            }));
        }
    };

    const addParticipantToBatch = () => {
        setBatchParticipants(prev => [...prev, { ...newParticipant, _id: new Date().toISOString() }]);
        setNewParticipant({ _id: '', name: '', phone: '', spots: 1, fee: 0, checkedIn: false, paid: false, approved: true });
    };

    const handleBatchEnroll = async () => {
        const totalRequestedSpots = batchParticipants.reduce((sum, p) => sum + p.spots, 0);
        if (totalRequestedSpots > availableSpots) {
            setError(`沒有足夠的名額。只剩下 ${availableSpots} 個名額。`);
            return;
        }

        try {
            const response = await axios.post(
                `${API_BASE_URL}/events/${editedEvent._id}/participants/batch-enroll`,
                { participants: batchParticipants },
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            setEditedEvent(response.data.event);
            onEventUpdated(editedEvent);
            setSuccess('批量參加者已成功加入活動');
            setBatchParticipants([]);
            setAvailableSpots(response.data.event.maxParticipants - (Array.isArray(response.data.event.participants) ? response.data.event.participants.reduce((sum: number, p: { spots: number }) => sum + p.spots, 0) : 0));
        } catch (error) {
            console.error('批量加入參加者時發生錯誤:', error);
            setError('批量加入參加者失敗。請稍後再試。');
        }
    };

    const handleRemoveWaitingParticipant = async (participantId: string) => {
        try {
            const response = await axios.delete(
                `${API_BASE_URL}/events/${editedEvent._id}/waiting-list/${participantId}`,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            setEditedEvent(response.data.event);
            onEventUpdated(response.data.event);
            setSuccess('已從候補名單移除參與者');
        } catch (error) {
            console.error('Error removing waiting participant:', error);
            setError('移除候補名單參與者失敗。請稍後再試。');
        }
    };

    const handleApproveParticipant = async (participantId: string) => {
        try {
            const response = await axios.put(
                `${API_BASE_URL}/events/${editedEvent._id}/participants/${participantId}/approve`,
                undefined,
                { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
            );
            setEditedEvent(response.data.event);
            onEventUpdated(response.data.event);
            setSuccess('參與者已核准並移至參加者名單');
        } catch (error) {
            console.error('Error approving participant:', error);
            setError('核准參與者失敗。請稍後再試。');
        }
    };

    const renderEventDetailsTab = () => (
        <form>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<ContentCopyIcon />}
                    onClick={() => setShowDuplicateDialog(true)}
                >
                    複製活動
                </Button>
            </Box>
            <FormControl component="fieldset" fullWidth margin="dense">
                <Typography variant="subtitle2" gutterBottom>活動類型</Typography>
                <RadioGroup
                  row
                  name="type"
                  value={editedEvent.type || 'normal'}
                  onChange={handleInputChange}
                >
                  <FormControlLabel value="normal" control={<Radio />} label="普通活動" />
                  <FormControlLabel value="tournament" control={<Radio />} label="比賽" />
                </RadioGroup>
            </FormControl>
            <FormControlLabel
                control={
                    <Switch
                        checked={isCustomLocation}
                        onChange={(e) => {
                            setIsCustomLocation(e.target.checked);
                            if (!e.target.checked) {
                                setCustomLocation('');
                            }
                        }}
                        name="isCustomLocation"
                        color="primary"
                    />
                }
                label="自訂地點"
                sx={{ mb: 2 }}
            />
            {isCustomLocation ? (
                <TextField
                    margin="dense"
                    label="自訂地點"
                    fullWidth
                    variant="outlined"
                    value={customLocation}
                    onChange={(e) => {
                        const value = e.target.value;
                        setCustomLocation(value);
                        setEditedEvent(prev => ({ ...prev, location: value }));
                    }}
                    required
                    placeholder="請輸入地點"
                    autoFocus
                />
            ) : (
                <FormControl fullWidth margin="dense">
                    <InputLabel id="venue-label">場地</InputLabel>
                    <Select
                        labelId="venue-label"
                        name="location"
                        value={editedEvent.location}
                        label="場地"
                        onChange={handleInputChange}
                        required
                    >
                        {Object.entries(groupedVenues).map(([distCode, venues]) => [
                            <ListSubheader key={distCode}>{distCode}</ListSubheader>,
                            ...venues.map((venue) => (
                                <MenuItem key={venue.venueName} value={venue.venueName}>
                                    {venue.venueName}
                                </MenuItem>
                            ))
                        ]).flat()}
                    </Select>
                </FormControl>
            )}
            <TextField
                margin="dense"
                name="date"
                label="日期"
                type="date"
                fullWidth
                variant="outlined"
                value={editedEvent.date}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
            />
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="from-hour-label">開始時間</InputLabel>
                        <Select
                            labelId="from-hour-label"
                            name="fromHour"
                            value={editedEvent.time.split(' - ')[0].split(':')[0]}
                            label="開始時間"
                            onChange={handleInputChange}
                            required
                        >
                            {hours.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                    {hour}:00
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="to-hour-label">結束時間</InputLabel>
                        <Select
                            labelId="to-hour-label"
                            name="toHour"
                            value={editedEvent.time.split(' - ')[1].split(':')[0]}
                            label="結束時間"
                            onChange={handleInputChange}
                            required
                        >
                            {hours.map((hour) => (
                                <MenuItem key={hour} value={hour}>
                                    {hour}:00
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        margin="dense"
                        name="maxParticipants"
                        label="最大參與人數"
                        type="number"
                        fullWidth
                        variant="outlined"
                        value={editedEvent.maxParticipants}
                        onChange={handleInputChange}
                        inputProps={{ min: 1, step: 1 }}
                        required
                    />
                </Grid>
            </Grid>
            
            <FormControlLabel
                control={
                    <Switch
                        checked={editedEvent.splitFee || false}
                        onChange={handleInputChange}
                        name="splitFee"
                        color="primary"
                    />
                }
                label="平分費用"
                sx={{ mt: 2, mb: 1 }}
            />
            
            {editedEvent.splitFee ? (
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            margin="dense"
                            name="totalCost"
                            label="總費用（將平分給所有參與者）"
                            fullWidth
                            variant="outlined"
                            value={editedEvent.totalCost || 0}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                </Grid>
            ) : (
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <TextField
                            margin="dense"
                            name="memberFee"
                            label="會員費用"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={editedEvent.memberFee}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            margin="dense"
                            name="normalFee"
                            label="非會員費用"
                            type="number"
                            fullWidth
                            variant="outlined"
                            value={editedEvent.normalFee}
                            onChange={handleInputChange}
                            required
                        />
                    </Grid>
                </Grid>
            )}
            <TextField
                margin="dense"
                name="otherDetails"
                label="其他詳情 (於網頁顯示)"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={editedEvent.otherDetails || ''}
                onChange={handleInputChange}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={editedEvent.isSendWhatsapp}
                        onChange={handleInputChange}
                        name="isSendWhatsapp"
                        color="primary"
                    />
                }
                label={`參加者加入時自動發送 WhatsApp 訊息`}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={editedEvent.isPrivate || false}
                        onChange={handleInputChange}
                        name="isPrivate"
                        color="primary"
                        disabled={editedEvent.isPublic || false}
                    />
                }
                label="私人活動（只有管理員和參與者可見，可透過連結加入）"
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={editedEvent.isPublic || false}
                        onChange={handleInputChange}
                        name="isPublic"
                        color="primary"
                        disabled={editedEvent.isPrivate || false}
                    />
                }
                label="公開於 MatchPoint 首頁"
            />
            <TextField
                margin="dense"
                name="sendDetails"
                label="Whatsapp 詳情"
                multiline
                rows={4}
                fullWidth
                variant="outlined"
                value={editedEvent.sendDetails || ''}
                onChange={handleInputChange}
            />
            
            {/* WhatsApp Join Event Message Preview */}
            {editedEvent.isSendWhatsapp && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        參加者加入時收到的訊息預覽：
                    </Typography>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            p: 2, 
                            bgcolor: '#f5f5f5', 
                            fontFamily: 'monospace',
                            whiteSpace: 'pre-line',
                            mb: 2,
                            fontSize: '0.9rem'
                        }}
                    >
                        {renderJoinEventPreviewInline()}
                    </Paper>
                </Box>
            )}
        </form>
    );

    const renderParticipantsTab = () => (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {editedEvent.date} 參與者名單 ({editedEvent.participants.length})
                </Typography>
                <Button
                    size="small"
                    onClick={() => setShowEditDelete(!showEditDelete)}
                    variant="outlined"
                >
                    {showEditDelete ? '隱藏編輯' : '顯示編輯'}
                </Button>
            </Box>
            <List sx={{ py: 0 }}>
                {editedEvent.participants.map((participant) => (
                    <ListItem
                        key={participant._id}
                        alignItems="flex-start"
                        sx={{
                            flexDirection: 'column',
                            py: 2,
                            borderBottom: '1px solid #e0e0e0',
                            '&:last-child': { borderBottom: 'none' }
                        }}
                    >
                        <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                            <IconButton
                                aria-label="check-in"
                                onClick={() => handleCheckIn(participant._id)}
                                sx={{ mr: 1, color: participant.checkedIn ? green[500] : 'default' }}
                            >
                                <CheckCircleOutlineIcon fontSize="large" />
                            </IconButton>
                            <ListItemText
                                primary={`${participant.name} (${participant.spots} 位) | 費用: ${participant.fee}`}
                                secondary={
                                    <Link href={getWhatsAppLink(participant.phone)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <WhatsAppIcon sx={{ mr: 0.5 }} />
                                    {participant.phone}
                                    </Link>
                                }
                            />
                            <IconButton
                                aria-label="paid"
                                onClick={() => handlePaid(participant._id)}
                                sx={{ color: participant.paid ? green[500] : 'default' }}
                            >
                                <MoneyIcon fontSize="large" />
                            </IconButton>
                            {!participant.approved && (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    onClick={() => handleApproveParticipant(participant._id)}
                                    size="small"
                                    sx={{ ml: 1 }}
                                >
                                    核准
                                </Button>
                            )}
                        </Box>
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                            {showEditDelete && (
                                <Box>
                                    <IconButton aria-label="edit" onClick={() => setEditingParticipant(participant)}>
                                        <EditIcon fontSize="large" />
                                    </IconButton>
                                    <IconButton aria-label="delete" onClick={() => setParticipantToRemove(participant)}>
                                        <DeleteIcon fontSize="large" />
                                    </IconButton>
                                </Box>
                            )}
                        </Box>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    const renderWaitingListTab = () => {
        const approvalParticipants = editedEvent.waitingList.filter(p => p.approved === false);
        
        return (
            <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">
                        候補名單 ({editedEvent.waitingList.length})
                    </Typography>
                    <Button
                        size="small"
                        onClick={() => setShowEditDelete(!showEditDelete)}
                        variant="outlined"
                    >
                        {showEditDelete ? '隱藏編輯' : '顯示編輯'}
                    </Button>
                </Box>
                
                {approvalParticipants.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h6" gutterBottom color="primary">
                            等待核准的參與者 ({approvalParticipants.length})
                        </Typography>
                        <List>
                            {approvalParticipants.map((participant) => (
                                <ListItem 
                                    key={participant._id}
                                    secondaryAction={
                                        <Box>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handleApproveParticipant(participant._id)}
                                                size="small"
                                            >
                                                核准
                                            </Button>
                                            <IconButton 
                                                edge="end" 
                                                aria-label="delete"
                                                onClick={() => handleRemoveWaitingParticipant(participant._id)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    }
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                <Typography variant="body1">{participant.name}</Typography>
                                                <Chip 
                                                    label={`${participant.spots}人`} 
                                                    size="small" 
                                                    sx={{ ml: 1 }}
                                                />
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                <Typography variant="body2" component="span">
                                                    {participant.phone && (
                                                        <Link 
                                                            href={getWhatsAppLink(participant.phone)} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            sx={{ mr: 2 }}
                                                        >
                                                            {participant.phone}
                                                        </Link>
                                                    )}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
                
                <Typography variant="h6" gutterBottom>
                    候補名單
                </Typography>
                <List sx={{ py: 0 }}>
                    {editedEvent.waitingList.map((participant) => (
                        <ListItem
                            key={participant._id}
                            alignItems="flex-start"
                            sx={{
                                flexDirection: 'column',
                                py: 2,
                                borderBottom: '1px solid #e0e0e0',
                                '&:last-child': { borderBottom: 'none' }
                            }}
                        >
                            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center' }}>
                                <ListItemText
                                    primary={`${participant.name} (${participant.spots} 位)`}
                                    secondary={
                                        <Link href={getWhatsAppLink(participant.phone)} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                            <WhatsAppIcon sx={{ mr: 0.5 }} />
                                            {participant.phone}
                                        </Link>
                                    }
                                />
                                {participant.approved && (
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleMoveFromWaitingList(participant._id)}
                                        sx={{ ml: 2 }}
                                    >
                                        移至參加者
                                    </Button>
                                )}
                                {!participant.approved && (
                                    <Typography>未核准</Typography>
                                )}
                            </Box>
                            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center' }}>
                                {showEditDelete && (
                                    <Box>
                                        <IconButton aria-label="edit" onClick={() => setEditingParticipant(participant)}>
                                            <EditIcon fontSize="large" />
                                        </IconButton>
                                        <IconButton aria-label="delete" onClick={() => handleRemoveWaitingParticipant(participant._id)}>
                                            <DeleteIcon fontSize="large" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </ListItem>
                    ))}
                </List>
            </Box>
        );
    };

    const renderBroadcastTab = () => {
        const unpaidParticipants = editedEvent.participants.filter(p => !p.paid);
        
        return (
            <Box>
                <Typography variant="h6" gutterBottom>
                    廣播訊息
                </Typography>
                <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={broadcastMessage}
                    onChange={(e) => setBroadcastMessage(e.target.value)}
                    placeholder="輸入給所有參加者的廣播訊息，例如：請於11月1日早上9時到屯門大興羽毛球場1號場見。"
                    disabled={isBroadcasting}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<PreviewIcon />}
                        onClick={() => setShowBroadcastPreview(true)}
                        disabled={isBroadcasting || !broadcastMessage.trim()}
                    >
                        預覽訊息
                    </Button>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={isBroadcasting ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                    onClick={handleBroadcast}
                    disabled={isBroadcasting}
                >
                    {isBroadcasting ? '傳送中...' : '傳送訊息'}
                </Button>
                </Box>

                {/* Payment reminder section - only show if user has permission */}
                {sitePermissions?.canSendPaymentReminder && (
                    <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="h6" gutterBottom>
                            付款提醒
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                            發送付款連結給未付款的參加者 ({unpaidParticipants.length} 位)
                        </Typography>
                        <Button
                            variant="outlined"
                            color="secondary"
                            startIcon={<MoneyIcon />}
                            onClick={handleShowPaymentPreview}
                            disabled={unpaidParticipants.length === 0}
                            sx={{ mt: 1 }}
                        >
                            預覽並發送付款連結
                        </Button>
                        {unpaidParticipants.length === 0 && (
                            <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                所有參加者都已付款！
                            </Typography>
                        )}
                    </Box>
                )}

                {/* Reminder Message Section - info + preview only */}
                <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" gutterBottom>
                        活動提醒
                    </Typography>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        活動提醒訊息將由系統自動於活動前一天中午12時自動發送。
                    </Alert>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            預設提醒訊息預覽：
                        </Typography>
                        <Paper 
                            variant="outlined" 
                            sx={{ 
                                p: 2, 
                                bgcolor: '#f5f5f5', 
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-line',
                                mb: 2,
                                fontSize: '0.9rem'
                            }}
                        >
                            {/* Render the preview for the first participant */}
                            {editedEvent.participants.length > 0 ? (
                                renderReminderPreviewInline()
                            ) : (
                                '無參加者可預覽訊息'
                            )}
                        </Paper>
                    </Box>
                </Box>
            </Box>
        );
    };

    const renderBatchEnrollmentTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                批量加入參加者
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
                可用名額：{availableSpots}
            </Typography>
            
            <Autocomplete
                options={previousParticipants}
                getOptionLabel={(option: string | PreviousParticipant) => 
                    typeof option === 'string' ? option : option.name
                }
                onChange={handleNameSelect}
                loading={isLoadingPreviousParticipants}
                freeSolo
                renderInput={(params) => (
                    <TextField
                        {...params}
                        margin="dense"
                        label="姓名"
                        fullWidth
                        value={newParticipant.name}
                        onChange={handleBatchParticipantChange}
                        name="name"
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isLoadingPreviousParticipants ? <CircularProgress color="inherit" size={20} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />
            
            <TextField
                margin="dense"
                name="phone"
                label="電話"
                fullWidth
                value={newParticipant.phone}
                onChange={handleBatchParticipantChange}
            />
            
            <TextField
                margin="dense"
                name="spots"
                label="人數"
                type="number"
                fullWidth
                value={newParticipant.spots}
                onChange={handleBatchParticipantChange}
            />
            
            <Button variant="contained" color="primary" onClick={addParticipantToBatch} sx={{ mt: 2 }}>
                添加參加者
            </Button>
            
            {batchParticipants.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        待加入的參加者：
                    </Typography>
                    <List>
                        {batchParticipants.map((participant, index) => (
                            <ListItem key={index}>
                                <ListItemText primary={`${participant.name} (${participant.spots} 位)`} secondary={`電話: ${participant.phone}`} />
                            </ListItem>
                        ))}
                    </List>
                    <Button variant="contained" color="primary" onClick={handleBatchEnroll} sx={{ mt: 2 }}>
                        批量加入
                    </Button>
                </Box>
            )}
        </Box>
    );

    const handleCreateAnnouncement = async () => {
        if (!announcementTitle.trim() || !announcementMessage.trim()) {
            setError('請填寫公告標題和內容');
            return;
        }

        try {
            setIsCreatingAnnouncement(true);
            setError(null);
            
            const response = await axios.post(
                `${API_BASE_URL}/events/${editedEvent._id}/announcements`,
                {
                    title: announcementTitle,
                    message: announcementMessage,
                    isImportant: isImportant,
                    visibility: announcementVisibility
                },
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }
            );

            // Update the event with the new announcement
            const updatedEvent = {
                ...editedEvent,
                announcements: [...(editedEvent.announcements || []), response.data.announcement]
            };
            setEditedEvent(updatedEvent);
            onEventUpdated(updatedEvent);

            // Reset form
            setAnnouncementTitle('');
            setAnnouncementMessage('');
            setIsImportant(false);
            setAnnouncementVisibility('public');
            setSuccess('公告已發布');
        } catch (err: any) {
            setError(err.response?.data?.message || '發布公告失敗');
        } finally {
            setIsCreatingAnnouncement(false);
        }
    };

    const handleDeleteAnnouncement = async (announcementId: string) => {
        try {
            await axios.delete(
                `${API_BASE_URL}/events/${editedEvent._id}/announcements/${announcementId}`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                }
            );

            // Update the event by removing the announcement
            const updatedEvent = {
                ...editedEvent,
                announcements: (editedEvent.announcements || []).filter(a => a._id !== announcementId)
            };
            setEditedEvent(updatedEvent);
            onEventUpdated(updatedEvent);
            setSuccess('公告已刪除');
        } catch (err: any) {
            setError(err.response?.data?.message || '刪除公告失敗');
        }
    };

    const renderAnnouncementsTab = () => (
        <Box>
            <Typography variant="h6" gutterBottom>
                發布公告
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                公開公告所有人可見，私密公告僅已報名參加者可見
            </Typography>

            <TextField
                margin="dense"
                label="公告標題"
                fullWidth
                value={announcementTitle}
                onChange={(e) => setAnnouncementTitle(e.target.value)}
                placeholder="例如：場地變更通知"
            />

            <TextField
                margin="dense"
                label="公告內容"
                fullWidth
                multiline
                rows={4}
                value={announcementMessage}
                onChange={(e) => setAnnouncementMessage(e.target.value)}
                placeholder="輸入公告詳細內容..."
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={isImportant}
                        onChange={(e) => setIsImportant(e.target.checked)}
                        color="error"
                    />
                }
                label="標記為重要公告"
                sx={{ mt: 1 }}
            />

            <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>可見性</InputLabel>
                <Select
                    value={announcementVisibility}
                    onChange={(e) => setAnnouncementVisibility(e.target.value as 'public' | 'private')}
                    label="可見性"
                >
                    <MenuItem value="public">公開 - 所有人可見</MenuItem>
                    <MenuItem value="private">私密 - 僅已報名參加者可見</MenuItem>
                </Select>
            </FormControl>

            <Button
                variant="contained"
                color="primary"
                onClick={handleCreateAnnouncement}
                disabled={isCreatingAnnouncement || !announcementTitle.trim() || !announcementMessage.trim()}
                startIcon={isCreatingAnnouncement ? <CircularProgress size={20} /> : <CampaignIcon />}
                sx={{ mt: 2 }}
            >
                {isCreatingAnnouncement ? '發布中...' : '發布公告'}
            </Button>

            {/* Existing Announcements */}
            {editedEvent.announcements && editedEvent.announcements.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" gutterBottom>
                        已發布的公告 ({editedEvent.announcements.length})
                    </Typography>
                    <List>
                        {editedEvent.announcements.map((announcement) => (
                            <Paper key={announcement._id} sx={{ p: 2, mb: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {announcement.title}
                                            </Typography>
                                            {announcement.isImportant && (
                                                <Chip label="重要" color="error" size="small" />
                                            )}
                                            {announcement.visibility === 'private' && (
                                                <Chip label="私密" color="secondary" size="small" />
                                            )}
                                        </Box>
                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                            {announcement.message}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {new Date(announcement.createdAt).toLocaleString('zh-TW')}
                                        </Typography>
                                    </Box>
                                    <IconButton
                                        onClick={() => handleDeleteAnnouncement(announcement._id)}
                                        color="error"
                                        size="small"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Paper>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );

    const renderReminderPreviewInline = () => {
        const firstParticipant = editedEvent.participants[0];
        if (!firstParticipant) return '無參加者可預覽訊息';

        // If siteSettings is still loading, show loading message
        if (!siteSettings) {
            return '正在載入群組設定...';
        }

        // Generate a simple reminder preview
        const contactNumber = siteSettings.defaultSenderId || siteSettings.defaultSenderName || '系統管理員';
        const reminderMessage = `提提您：明天${editedEvent.time || '活動時間'}(${editedEvent.date || '活動日期'})於${editedEvent.location || '活動地點'}${editedEvent.sendDetails || ''}。如有任何問題，請隨時聯繫${contactNumber}。謝謝！`;
        
        return `[來自${siteName}的訊息] 
你好 ${firstParticipant.name}，${reminderMessage}`;
    };

    const renderJoinEventPreviewInline = () => {
        // If siteSettings is still loading, show loading message
        if (!siteSettings) {
            return '正在載入群組設定...';
        }

        // Use the joinEventPreview that was already fetched from API
        if (joinEventPreview && joinEventPreview !== '載入中...') {
            return joinEventPreview; // API response already includes proper formatting
        }

        // If still loading, show loading message
        return '載入中...';
    };

    // Navigation model: three top-level categories, each with one or more views.
    // The "when to use this" description is shown above every view.
    const categories: Array<{
        key: string;
        label: string;
        icon: React.ReactElement;
        views: Array<{ key: ViewKey; label: string; icon: React.ReactElement; count?: number; description: string }>;
    }> = [
        {
            key: 'event',
            label: '活動',
            icon: <EventIcon />,
            views: [
                { key: 'details', label: '活動詳情', icon: <EventIcon />, description: '編輯活動的日期、時間、地點、費用與其他設定，完成後按右上角儲存。' }
            ]
        },
        {
            key: 'registration',
            label: '報名',
            icon: <PeopleIcon />,
            views: [
                { key: 'participants', label: '參加者', icon: <PeopleIcon />, count: editedEvent.participants.length, description: '管理已報名的參加者：簽到、標記付款、編輯或移除。' },
                { key: 'waiting', label: '候補名單', icon: <QueueIcon />, count: editedEvent.waitingList.length, description: '活動滿額時的後備名單，可核准或將參加者遞補至正取。' },
                { key: 'batch', label: '批量加入', icon: <GroupAddIcon />, description: '一次過手動加入多位參加者（例如替朋友代為報名）。' }
            ]
        },
        {
            key: 'comms',
            label: '通知',
            icon: <CampaignIcon />,
            views: [
                { key: 'broadcast', label: '廣播 / 付款', icon: <BroadcastOnPersonalIcon />, description: '透過 WhatsApp 向所有參加者發送訊息，或發送付款提醒連結。' },
                { key: 'announcements', label: '公告', icon: <CampaignIcon />, count: editedEvent.announcements?.length || 0, description: '在活動頁面向參加者顯示的文字公告。' }
            ]
        }
    ];

    const activeCategoryIndex = Math.max(0, categories.findIndex(c => c.views.some(v => v.key === view)));
    const activeCategory = categories[activeCategoryIndex];
    const activeViewMeta = activeCategory.views.find(v => v.key === view) || activeCategory.views[0];

    const renderActiveView = () => {
        switch (view) {
            case 'details': return renderEventDetailsTab();
            case 'participants': return renderParticipantsTab();
            case 'waiting': return renderWaitingListTab();
            case 'batch': return renderBatchEnrollmentTab();
            case 'broadcast': return renderBroadcastTab();
            case 'announcements': return renderAnnouncementsTab();
            default: return null;
        }
    };

    return (
        <Dialog
            fullScreen
            open={true}
            onClose={onClose}
            disableScrollLock={false}
            sx={{
                '& .MuiDialog-paper': {
                    margin: 0,
                }
            }}
        >
            <AppBar sx={{ position: 'relative', paddingTop: 'env(safe-area-inset-top)' }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        aria-label="關閉"
                        disabled={isSaving || isDeleting}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                        管理活動
                    </Typography>
                    <Button onClick={() => setShowDeleteConfirmation(true)} color="inherit" disabled={isSaving || isDeleting}>
                        <DeleteIcon />
                    </Button>
                    {view === 'details' && (
                        <Button color="inherit" onClick={handleSave} disabled={isSaving} startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}>
                            儲存
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <DialogContent ref={dialogContentRef} sx={{ p: 0, pb: 8, display: 'flex', flexDirection: 'column', height: '100%' }}>
                {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ m: 2 }}>{success}</Alert>}

                <Box sx={{ width: '100%', flexGrow: 1, overflow: 'auto' }}>
                    {/* Secondary navigation: only shown when the active category has
                        more than one view (報名 / 通知). */}
                    {activeCategory.views.length > 1 && (
                        <Box sx={{ borderBottom: 1, borderColor: 'divider', position: 'sticky', top: 0, zIndex: 2, backgroundColor: 'background.paper' }}>
                            <Tabs
                                value={view}
                                onChange={(_, v: ViewKey) => setView(v)}
                                variant="fullWidth"
                            >
                                {activeCategory.views.map(v => (
                                    <Tab
                                        key={v.key}
                                        value={v.key}
                                        icon={v.icon}
                                        iconPosition="start"
                                        label={v.count !== undefined ? `${v.label} (${v.count})` : v.label}
                                        sx={{ minHeight: 56 }}
                                    />
                                ))}
                            </Tabs>
                        </Box>
                    )}

                    {/* "When to use this" helper shown for every view. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.5, bgcolor: 'action.hover' }}>
                        <InfoOutlinedIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                            {activeViewMeta.description}
                        </Typography>
                    </Box>

                    <Box sx={{ p: 3 }}>
                        {renderActiveView()}
                    </Box>
                </Box>

                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 1000,
                        backgroundColor: 'background.paper',
                        borderTop: '1px solid #e0e0e0',
                        paddingBottom: 'max(env(safe-area-inset-bottom), 0px)',
                        transform: 'translateZ(0)',
                        WebkitTransform: 'translateZ(0)',
                        willChange: 'transform'
                    }}
                >
                    <Tabs
                        value={activeCategoryIndex}
                        onChange={(_, idx: number) => setView(categories[idx].views[0].key)}
                        variant="fullWidth"
                        aria-label="管理活動分類"
                        sx={{ minHeight: 64 }}
                    >
                        {categories.map(category => (
                            <Tab
                                key={category.key}
                                icon={category.icon}
                                label={category.label}
                                sx={{ minHeight: 64 }}
                            />
                        ))}
                    </Tabs>
                </Box>

                <Dialog open={!!editingParticipant} onClose={() => setEditingParticipant(null)}>
                    <DialogTitle>編輯參與者</DialogTitle>
                    <DialogContent>
                        <TextField
                            margin="dense"
                            name="name"
                            label="姓名"
                            fullWidth
                            value={editingParticipant?.name || ''}
                            onChange={handleParticipantInputChange}
                        />
                        <TextField
                            margin="dense"
                            name="phone"
                            label="電話"
                            fullWidth
                            value={editingParticipant?.phone || ''}
                            onChange={handleParticipantInputChange}
                        />
                        <TextField
                            margin="dense"
                            name="spots"
                            label="人數"
                            type="number"
                            fullWidth
                            value={editingParticipant?.spots || ''}
                            onChange={handleParticipantInputChange}
                        />
                        <TextField
                            margin="dense"
                            name="fee"
                            label="費用"
                            type="number"
                            fullWidth
                            value={editingParticipant?.fee || ''}
                            onChange={handleParticipantInputChange}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditingParticipant(null)} disabled={isParticipantSaving}>取消</Button>
                        <Button onClick={handleSaveParticipant} disabled={isParticipantSaving}>
                            {isParticipantSaving ? <CircularProgress size={24} /> : '保存'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </DialogContent>
                        {/* Payment Message Preview */}
            <WhatsAppMessagePreview
                open={showPaymentPreview}
                onClose={() => setShowPaymentPreview(false)}
                onConfirm={handleSendPaymentLinks}
                templateId="payment"
                participants={editedEvent.participants.filter(p => !p.paid).map(p => ({
                    _id: p._id,
                    name: p.name,
                    phone: p.phone,
                    spots: p.spots,
                    fee: p.fee,
                    checkedIn: p.checkedIn,
                    paid: p.paid,
                    approved: p.approved
                }))}
                event={editedEvent}
                siteSettings={{
                    defaultSenderId: siteSettings?.defaultSenderId,
                    defaultSenderName: siteSettings?.defaultSenderName,
                    paymentLink: siteSettings?.paymentLink,
                    whatsappMessageTemplate: siteSettings?.whatsappMessageTemplate
                }}
                siteName={siteName}
                title="付款訊息預覽"
                confirmButtonText="確認發送付款連結"
                isLoading={isSendingPaymentLinks}
            />

            <Dialog open={showDeleteConfirmation} onClose={() => setShowDeleteConfirmation(false)}>
                <DialogTitle>確認刪除</DialogTitle>
                <DialogContent>
                    <Typography>您確定要刪除這個活動嗎？此操作無法撤銷。</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowDeleteConfirmation(false)} disabled={isDeleting}>取消</Button>
                    <Button onClick={handleDelete} color="error" autoFocus disabled={isDeleting}>
                        {isDeleting ? <CircularProgress size={24} /> : '刪除'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Duplicate / Recurring Event Dialog */}
            <DuplicateEventModal
                event={editedEvent}
                open={showDuplicateDialog}
                onClose={() => setShowDuplicateDialog(false)}
                onDuplicated={() => {
                    setSuccess('已成功複製活動');
                    onEventUpdated(editedEvent);
                    if (dialogContentRef.current) {
                        dialogContentRef.current.scrollTop = 0;
                    }
                }}
            />

            {/* Remove Participant Confirmation */}
            <Dialog open={!!participantToRemove} onClose={() => setParticipantToRemove(null)} maxWidth="xs" fullWidth>
                <DialogTitle>移除參與者</DialogTitle>
                <DialogContent>
                    <Typography>
                        確定要將 <strong>{participantToRemove?.name}</strong> 從參與者名單中移除嗎？此操作無法撤銷。
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setParticipantToRemove(null)} disabled={isRemovingParticipant}>取消</Button>
                    <Button onClick={confirmRemoveParticipant} color="error" disabled={isRemovingParticipant}>
                        {isRemovingParticipant ? <CircularProgress size={24} /> : '移除'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Broadcast Message Preview */}
            <WhatsAppMessagePreview
                open={showBroadcastPreview}
                onClose={() => setShowBroadcastPreview(false)}
                onConfirm={handleBroadcastWithPreview}
                templateId="broadcast"
                participants={editedEvent.participants.map(p => ({
                    _id: p._id,
                    name: p.name,
                    phone: p.phone,
                    spots: p.spots,
                    fee: p.fee,
                    checkedIn: p.checkedIn,
                    paid: p.paid,
                    approved: p.approved
                }))}
                event={editedEvent}
                siteSettings={{
                    defaultSenderId: siteSettings?.defaultSenderId,
                    defaultSenderName: siteSettings?.defaultSenderName,
                    paymentLink: siteSettings?.paymentLink,
                    whatsappMessageTemplate: siteSettings?.whatsappMessageTemplate
                }}
                siteName={siteName}
                customMessage={broadcastMessage}
                title="廣播訊息預覽"
                confirmButtonText="確認發送"
                isLoading={isBroadcasting}
            />
        </Dialog>
    );
};

export default ManageEventModal;
